import {
    options_mailboxCleanup_EmptyFolderConfirmation,
    options_mailboxCleanup_EmptyFolderConfirmationSubtext,
    options_mailboxCleanup_ConfirmDeleteByDate,
    options_mailboxCleanup_DeleteDate,
    options_mailboxCleanup_ConfirmDeleteByDateSubtext,
} from 'owa-storage-option/lib/components/StorageOptionFull.locstring.json';
import loc, { format } from 'owa-localize';
import { emptyFolderService } from 'owa-folders/lib/services/emptyFolderService';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import BaseFolderType from 'owa-service/lib/contract/BaseFolderType';
import getFolderStorageInformation from '../actions/getFolderStorageInformation';
import checkIfResponseSuccess from 'owa-service-utils/lib/checkIfResponseSuccess';
import SingleResponseMessage from 'owa-service/lib/contract/SingleResponseMessage';
import { TraceErrorObject } from 'owa-trace';
import { lazyGetAccountInformation } from 'owa-storage-store';
import { lazyOnBulkActionSubmitted } from 'owa-bulk-action-store';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { subMonths, now } from 'owa-datetime';

export default function emptySelectedFolder(folder: BaseFolderType, monthsBefore?: number) {
    const lastSyncTime = monthsBefore ? subMonths(now(), monthsBefore) : null; // Having lastSyncTime as null is the same as now() on the service call.
    const folderTotalCount = folder.TotalCount;
    const folderName = folder.DisplayName;
    const confirmDelete = lastSyncTime
        ? confirm(
              format(loc(options_mailboxCleanup_ConfirmDeleteByDate), folderName),
              format(
                  loc(options_mailboxCleanup_ConfirmDeleteByDateSubtext),
                  format(loc(options_mailboxCleanup_DeleteDate), monthsBefore),
                  folderName
              ),
              false /* resolveImmediately */
          )
        : confirm(
              format(
                  loc(options_mailboxCleanup_EmptyFolderConfirmation),
                  folderTotalCount,
                  folderName
              ),
              format(loc(options_mailboxCleanup_EmptyFolderConfirmationSubtext), folderTotalCount),
              false /* resolveImmediately */
          );

    return confirmDelete.then(async (response: DialogResponse) => {
        if (response === DialogResponse.ok) {
            const deleteSubFolders = false;
            const disposalType = 'SoftDelete';
            lazyOnBulkActionSubmitted.importAndExecute(
                // Locally update the bulk action store. Before starting to empty folder.
                folder.FolderId.Id,
                'EmptyFolder' /* scenario */,
                null /* customData */,
                folderTotalCount /* totalCount */
            );
            const performanceDatapoint = new PerformanceDatapoint('TnS_EmptyFolderAction');
            performanceDatapoint.addCustomData({
                folderTotalCount,
                disposalType,
                deleteSubFolders,
                actionSource: 'MailboxCleanup',
            });

            emptyFolderService(
                folder, // Folder
                disposalType, // Disposal Type
                deleteSubFolders, // Delete Sub Folders
                lastSyncTime, // Client Last Sync Time
                [], // Item Id's to Exclude
                true // Run Bulk Action, will always be true for the displayed folders.
            )
                .then(response => {
                    // Get the top folders after the change
                    handleServerResponseSuccessAndError(response);
                    performanceDatapoint.end();
                    lazyGetAccountInformation.importAndExecute();
                    getFolderStorageInformation();
                })
                .catch(error => {
                    performanceDatapoint.endWithError(DatapointStatus.ServerError, error);
                    return Promise.reject(error);
                });
        }
    });
}

function handleServerResponseSuccessAndError(
    response: SingleResponseMessage | SingleResponseMessage[]
): Promise<void> {
    if (!response) {
        return Promise.reject(createServiceFetchError('500', 'Server returned a null response!'));
    }

    const responseItems = Array.isArray(response) ? response : [response];
    for (const responseItem of responseItems) {
        if (responseItem && !checkIfResponseSuccess(responseItem)) {
            return Promise.reject(
                createServiceFetchError(responseItem.ResponseCode, responseItem.StackTrace)
            );
        }
    }

    return Promise.resolve();
}

function createServiceFetchError(responseCode: string, stackTrace: string): Error {
    const error = new Error(
        `ResponseCode=${responseCode}, Stacktrace=${stackTrace}`
    ) as TraceErrorObject;

    error.fetchErrorType = 'ServerFailure';

    return error;
}
